import {
  Package,
  BarChart3,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Store,
  Brain,
  Percent,
  Shield,
  Clock,
  Zap,
  Target,
  ShoppingCart,
  Layers,
  CheckCircle2,
  AlertTriangle,
  Settings,
  Eye,
  Trash2,
  Power,
  Pencil,
  Sparkles,
  History,
  ListChecks,
  ArrowDown,
  Filter,
  Globe,
  Link,
  Scan,
  Bell,
  LineChart,
  Key,
  Upload,
  FileSpreadsheet,
  HelpCircle,
  ExternalLink,
  Layout,
  Users,
  DollarSign,
  FileText,
  Lock,
  UserCheck,
  UserX,
  MessageSquare,
  Calculator,
  Hash,
  ToggleLeft,
  Rows3,
  GitBranch,
  Database,
  Wifi,
  Unplug,
  Play,
  Code,
  RotateCcw,
} from 'lucide-react';

/* ─── Shared helpers ──────────────────────────────────────────── */
function MockCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-lg border bg-background p-3 text-center shadow-sm ${className}`}>
      {children}
    </div>
  );
}

function MockBadge({
  children,
  variant = 'default',
}: {
  children: React.ReactNode;
  variant?: string;
}) {
  const colors: Record<string, string> = {
    default: 'bg-primary/10 text-primary',
    green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    gray: 'bg-muted text-muted-foreground',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${colors[variant] || colors.default}`}
    >
      {children}
    </span>
  );
}

function MockButton({
  children,
  variant = 'primary',
}: {
  children: React.ReactNode;
  variant?: string;
}) {
  const colors =
    variant === 'primary'
      ? 'bg-primary text-primary-foreground'
      : 'border bg-background text-foreground';
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-medium ${colors}`}
    >
      {children}
    </span>
  );
}

function DiagramCaption({ children }: { children: React.ReactNode }) {
  return <p className="mt-2 text-center text-[11px] text-muted-foreground italic">{children}</p>;
}

/* ─── DIAGRAMS ────────────────────────────────────────────────── */

/** Smart Pricing — 3 tabs overview */
export function DiagramSmartPricingTabs() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Brain className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Smart Pricing</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[
          { icon: Package, label: 'Análisis de Catálogo', desc: 'Analiza todos tus productos' },
          { icon: Store, label: 'Comparar con Tienda', desc: 'vs un competidor' },
          { icon: Layers, label: 'Productos Vinculados', desc: 'Estrategias aplicadas' },
        ].map(({ icon: Icon, label, desc }) => (
          <div key={label} className="rounded-lg border bg-background p-2.5 text-center">
            <Icon className="h-5 w-5 mx-auto mb-1 text-primary" />
            <p className="text-[10px] font-semibold text-foreground leading-tight">{label}</p>
            <p className="text-[9px] text-muted-foreground mt-0.5">{desc}</p>
          </div>
        ))}
      </div>
      <DiagramCaption>Las 3 pestañas principales de Smart Pricing</DiagramCaption>
    </div>
  );
}

/** Catalog Analysis — Summary Cards */
export function DiagramCatalogCards() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Filter className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">
          Tarjetas de resumen (clickeables)
        </span>
      </div>
      <div className="grid grid-cols-4 gap-2">
        <MockCard className="ring-2 ring-primary">
          <p className="text-lg font-bold">2,871</p>
          <p className="text-[9px] text-muted-foreground">Productos analizados</p>
        </MockCard>
        <MockCard>
          <p className="text-lg font-bold">2,800</p>
          <p className="text-[9px] text-muted-foreground">Con recomendaciones</p>
        </MockCard>
        <MockCard>
          <p className="text-lg font-bold text-green-600">348</p>
          <p className="text-[9px] text-muted-foreground">Subir precio</p>
        </MockCard>
        <MockCard>
          <p className="text-lg font-bold text-blue-600">2,452</p>
          <p className="text-[9px] text-muted-foreground">Bajar precio</p>
        </MockCard>
      </div>
      <DiagramCaption>Haz clic en cualquier tarjeta para filtrar la tabla</DiagramCaption>
    </div>
  );
}

/** Catalog Analysis — Product table mockup */
export function DiagramCatalogTable() {
  const rows = [
    {
      name: 'Samsung Galaxy S24',
      sku: 'SAM-S24',
      cost: 'Bs 2,800',
      price: 'Bs 4,200',
      margin: '33.3%',
      rec: 'Bs 3,950',
      diff: '-5.9%',
      up: false,
    },
    {
      name: 'Laptop HP Pavilion',
      sku: 'HP-PAV15',
      cost: 'Bs 3,500',
      price: 'Bs 4,100',
      margin: '14.6%',
      rec: 'Bs 4,550',
      diff: '+11.0%',
      up: true,
    },
  ];
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="overflow-x-auto">
        <table className="w-full text-[10px]">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="p-1.5 w-6">
                <input type="checkbox" checked readOnly className="rounded h-3 w-3" />
              </th>
              <th className="p-1.5 text-left font-medium">Producto</th>
              <th className="p-1.5 text-right font-medium">Costo</th>
              <th className="p-1.5 text-right font-medium">Precio actual</th>
              <th className="p-1.5 text-center font-medium">Margen</th>
              <th className="p-1.5 w-4"></th>
              <th className="p-1.5 text-right font-medium">Recomendado</th>
              <th className="p-1.5 text-right font-medium">Cambio</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.sku} className="border-b">
                <td className="p-1.5">
                  <input type="checkbox" checked readOnly className="rounded h-3 w-3" />
                </td>
                <td className="p-1.5">
                  <div className="font-medium">{r.name}</div>
                  <div className="text-muted-foreground font-mono">{r.sku}</div>
                </td>
                <td className="p-1.5 text-right text-muted-foreground font-mono">{r.cost}</td>
                <td className="p-1.5 text-right font-mono">{r.price}</td>
                <td className="p-1.5 text-center">
                  <MockBadge variant={parseFloat(r.margin) < 15 ? 'red' : 'default'}>
                    {r.margin}
                  </MockBadge>
                </td>
                <td className="p-1.5 text-center">
                  <ArrowRight className="h-3 w-3 text-muted-foreground mx-auto" />
                </td>
                <td className="p-1.5 text-right font-semibold font-mono">{r.rec}</td>
                <td className="p-1.5 text-right">
                  <MockBadge variant={r.up ? 'green' : 'blue'}>
                    {r.up ? (
                      <TrendingUp className="h-2.5 w-2.5 mr-0.5" />
                    ) : (
                      <TrendingDown className="h-2.5 w-2.5 mr-0.5" />
                    )}
                    {r.diff}
                  </MockBadge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <DiagramCaption>Ejemplo de tabla de productos con precios recomendados</DiagramCaption>
    </div>
  );
}

/** Strategies — 5 strategy cards */
export function DiagramStrategies() {
  const strategies = [
    {
      icon: Target,
      name: 'Margen Objetivo',
      desc: 'Fijas tu % de ganancia',
      color: 'text-blue-600',
      example: 'Costo $100 + 25% = $133',
    },
    {
      icon: ArrowDown,
      name: 'Igualar al más barato',
      desc: 'Mismo precio que competencia',
      color: 'text-orange-600',
      example: 'Competidor $150 → Tu $150',
    },
    {
      icon: TrendingDown,
      name: 'Undercut',
      desc: 'X% más barato que competencia',
      color: 'text-red-600',
      example: 'Competidor $150 -5% → Tu $142',
    },
    {
      icon: Sparkles,
      name: 'Smart Pricing',
      desc: 'IA calcula el optimo',
      color: 'text-purple-600',
      example: 'Combina todo automáticamente',
    },
    {
      icon: Settings,
      name: 'Formula personalizada',
      desc: 'Tus propias reglas',
      color: 'text-gray-600',
      example: 'Según tu modelo de pricing',
    },
  ];
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Zap className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Las 5 estrategias de precio</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
        {strategies.map(({ icon: Icon, name, desc, color, example }) => (
          <div key={name} className="rounded-lg border bg-background p-2.5">
            <Icon className={`h-5 w-5 mb-1 ${color}`} />
            <p className="text-[10px] font-semibold text-foreground leading-tight">{name}</p>
            <p className="text-[9px] text-muted-foreground mt-0.5">{desc}</p>
            <p className="text-[9px] text-primary/80 mt-1 italic">{example}</p>
          </div>
        ))}
      </div>
      <DiagramCaption>Selecciona una estrategia en el dropdown "Simular estrategia"</DiagramCaption>
    </div>
  );
}

/** Compare with store — summary cards */
export function DiagramCompareCards() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Resumen de comparación</span>
      </div>
      <div className="grid grid-cols-4 gap-2">
        <MockCard>
          <p className="text-lg font-bold">156</p>
          <p className="text-[9px] text-muted-foreground">Comparados</p>
        </MockCard>
        <MockCard className="cursor-pointer hover:shadow-md">
          <p className="text-lg font-bold text-red-600">42</p>
          <p className="text-[9px] text-muted-foreground">Competidor más barato</p>
        </MockCard>
        <MockCard className="cursor-pointer hover:shadow-md">
          <p className="text-lg font-bold text-green-600">89</p>
          <p className="text-[9px] text-muted-foreground">Tu eres más barato</p>
        </MockCard>
        <MockCard className="cursor-pointer hover:shadow-md">
          <p className="text-lg font-bold text-gray-500">25</p>
          <p className="text-[9px] text-muted-foreground">Precio similar</p>
        </MockCard>
      </div>
      <DiagramCaption>Haz clic en las tarjetas para filtrar por categoría</DiagramCaption>
    </div>
  );
}

/** Linked products — table mockup */
export function DiagramLinkedProducts() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Layers className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Productos vinculados</span>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-3">
        <MockCard>
          <p className="text-sm font-bold">45</p>
          <p className="text-[9px] text-muted-foreground">Total vinculados</p>
        </MockCard>
        <MockCard>
          <p className="text-sm font-bold text-purple-600">38</p>
          <p className="text-[9px] text-muted-foreground">Precio actualizado</p>
        </MockCard>
        <MockCard>
          <p className="text-sm font-bold text-blue-600">3</p>
          <p className="text-[9px] text-muted-foreground">Estrategias en uso</p>
        </MockCard>
        <MockCard>
          <p className="text-sm font-bold text-green-600">12</p>
          <p className="text-[9px] text-muted-foreground">Últimas 24h</p>
        </MockCard>
      </div>
      <div className="rounded-lg border bg-background overflow-hidden">
        <div className="grid grid-cols-6 gap-1 p-2 bg-muted/50 text-[9px] font-medium">
          <span>Producto</span>
          <span>Estrategia</span>
          <span className="text-right">Precio</span>
          <span className="text-right">Sugerido</span>
          <span>Aplicado</span>
          <span>Acciones</span>
        </div>
        <div className="grid grid-cols-6 gap-1 p-2 border-t text-[9px]">
          <span className="font-medium">Galaxy S24</span>
          <span>
            <MockBadge variant="default">Smart Pricing</MockBadge>
          </span>
          <span className="text-right font-mono">Bs 3,950</span>
          <span className="text-right font-mono text-green-600">Bs 4,100 +3.8%</span>
          <span className="text-muted-foreground">hace 2h</span>
          <span className="flex gap-1">
            <History className="h-3 w-3 text-blue-500" />
            <Trash2 className="h-3 w-3 text-red-500" />
          </span>
        </div>
      </div>
      <DiagramCaption>Registro de productos con estrategia aplicada</DiagramCaption>
    </div>
  );
}

/** Discounts — form mockup */
export function DiagramDiscountForm() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="rounded-lg border bg-background p-4 max-w-sm mx-auto">
        <p className="text-xs font-semibold text-foreground mb-3">Nuevo descuento</p>
        <div className="space-y-2.5">
          <div>
            <label className="text-[10px] text-muted-foreground">Nombre</label>
            <div className="mt-0.5 rounded border p-1.5 text-[10px] bg-muted/20">
              Black Friday 15%
            </div>
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground">Tipo</label>
            <div className="mt-0.5 flex gap-3">
              <span className="flex items-center gap-1 text-[10px]">
                <span className="h-3 w-3 rounded-full border-2 border-primary bg-primary/20" />{' '}
                Porcentaje
              </span>
              <span className="flex items-center gap-1 text-[10px]">
                <span className="h-3 w-3 rounded-full border-2 border-muted" /> Monto fijo
              </span>
            </div>
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground">Valor</label>
            <div className="mt-0.5 flex items-center gap-1">
              <div className="rounded border p-1.5 text-[10px] bg-muted/20 flex-1">15</div>
              <span className="text-[10px] font-medium">%</span>
            </div>
          </div>
          <div className="rounded border-2 border-dashed border-green-300 bg-green-50 dark:bg-green-950/20 p-2">
            <p className="text-[10px] text-green-700 dark:text-green-400">
              Ejemplo: un producto de $100 quedaria en <strong>$85.00</strong>
            </p>
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground">Vigencia (opcional)</label>
            <div className="mt-0.5 grid grid-cols-2 gap-1.5">
              <div className="rounded border p-1.5 text-[10px] bg-muted/20">28/11/2026</div>
              <div className="rounded border p-1.5 text-[10px] bg-muted/20">30/11/2026</div>
            </div>
          </div>
          <details className="text-[10px]">
            <summary className="text-muted-foreground cursor-pointer hover:text-foreground">
              Guardrails avanzados
            </summary>
            <div className="mt-1.5 space-y-1.5 pl-2 border-l-2 border-primary/20">
              <div>
                <label className="text-muted-foreground">Margen mínimo tras aplicar</label>
                <div className="rounded border p-1 bg-muted/20 mt-0.5 flex items-center gap-1">
                  10 <span className="text-muted-foreground">%</span>
                </div>
              </div>
              <div>
                <label className="text-muted-foreground">Tope absoluto</label>
                <div className="rounded border p-1 bg-muted/20 mt-0.5 flex items-center gap-1">
                  200 <span className="text-muted-foreground">Bs</span>
                </div>
              </div>
            </div>
          </details>
          <div className="flex gap-2 pt-1">
            <MockButton variant="outline">Cancelar</MockButton>
            <MockButton>Crear descuento</MockButton>
          </div>
        </div>
      </div>
      <DiagramCaption>Formulario para crear un descuento nuevo</DiagramCaption>
    </div>
  );
}

/** Discounts — status badges */
export function DiagramDiscountStatuses() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Percent className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Estados de un descuento</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Activo', color: 'blue', icon: CheckCircle2, desc: 'Funcionando y disponible' },
          { label: 'Inactivo', color: 'gray', icon: Power, desc: 'Desactivado manualmente' },
          {
            label: 'Programado',
            color: 'yellow',
            icon: Clock,
            desc: 'Fecha de inicio en el futuro',
          },
          { label: 'Vencido', color: 'red', icon: AlertTriangle, desc: 'Ya paso la fecha de fin' },
        ].map(({ label, color, icon: Icon, desc }) => (
          <div key={label} className="rounded-lg border bg-background p-2.5 text-center">
            <Icon
              className={`h-5 w-5 mx-auto mb-1 ${
                color === 'blue'
                  ? 'text-blue-500'
                  : color === 'gray'
                    ? 'text-gray-400'
                    : color === 'yellow'
                      ? 'text-yellow-500'
                      : 'text-red-500'
              }`}
            />
            <MockBadge variant={color}>{label}</MockBadge>
            <p className="text-[9px] text-muted-foreground mt-1">{desc}</p>
          </div>
        ))}
      </div>
      <DiagramCaption>
        El estado se calcula automáticamente según fechas y activación
      </DiagramCaption>
    </div>
  );
}

/** Discount actions */
export function DiagramDiscountActions() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Settings className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Acciones por descuento</span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[
          {
            icon: Pencil,
            label: 'Editar',
            desc: 'Modificar nombre, valor, fechas',
            color: 'text-foreground',
          },
          {
            icon: Power,
            label: 'Activar / Desactivar',
            desc: 'Prender o apagar con confirmación',
            color: 'text-yellow-600',
          },
          {
            icon: Trash2,
            label: 'Borrar',
            desc: 'Solo si no esta en uso por reglas',
            color: 'text-red-600',
          },
        ].map(({ icon: Icon, label, desc, color }) => (
          <div key={label} className="flex items-start gap-2 rounded-lg border bg-background p-2.5">
            <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${color}`} />
            <div>
              <p className="text-[10px] font-semibold">{label}</p>
              <p className="text-[9px] text-muted-foreground">{desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Full flow — 3 modules working together */
export function DiagramFullFlow() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">
          Como trabajan juntos los 3 modulos
        </span>
      </div>
      <div className="flex flex-col sm:flex-row items-center gap-2">
        <div className="rounded-lg border-2 border-purple-300 bg-purple-50 dark:bg-purple-950/20 p-3 text-center flex-1 w-full">
          <Percent className="h-6 w-6 mx-auto mb-1 text-purple-600" />
          <p className="text-[11px] font-semibold text-purple-700 dark:text-purple-400">
            1. Descuentos
          </p>
          <p className="text-[9px] text-muted-foreground">Crea descuentos reutilizables</p>
        </div>
        <ArrowRight className="h-5 w-5 text-muted-foreground shrink-0 rotate-90 sm:rotate-0" />
        <div className="rounded-lg border-2 border-blue-300 bg-blue-50 dark:bg-blue-950/20 p-3 text-center flex-1 w-full">
          <ListChecks className="h-6 w-6 mx-auto mb-1 text-blue-600" />
          <p className="text-[11px] font-semibold text-blue-700 dark:text-blue-400">2. Reglas</p>
          <p className="text-[9px] text-muted-foreground">Ponen los límites en %</p>
        </div>
        <ArrowRight className="h-5 w-5 text-muted-foreground shrink-0 rotate-90 sm:rotate-0" />
        <div className="rounded-lg border-2 border-green-300 bg-green-50 dark:bg-green-950/20 p-3 text-center flex-1 w-full">
          <Brain className="h-6 w-6 mx-auto mb-1 text-green-600" />
          <p className="text-[11px] font-semibold text-green-700 dark:text-green-400">
            3. Smart Pricing
          </p>
          <p className="text-[9px] text-muted-foreground">Calcula + aplica todo junto</p>
        </div>
        <ArrowRight className="h-5 w-5 text-muted-foreground shrink-0 rotate-90 sm:rotate-0" />
        <div className="rounded-lg border-2 border-orange-300 bg-orange-50 dark:bg-orange-950/20 p-3 text-center flex-1 w-full">
          <ShoppingCart className="h-6 w-6 mx-auto mb-1 text-orange-600" />
          <p className="text-[11px] font-semibold text-orange-700 dark:text-orange-400">
            4. Tu Tienda
          </p>
          <p className="text-[9px] text-muted-foreground">Precios actualizados</p>
        </div>
      </div>
      <DiagramCaption>
        Las reglas ponen los límites, Smart Pricing decide el precio dentro de ellos y actualiza tu
        tienda; los descuentos van directo a tu tienda
      </DiagramCaption>
    </div>
  );
}

/* ─── PRICING DINÁMICO ────────────────────────────────────────── */

/** Pricing Dinámico — 7 tabs overview */
export function DiagramDynamicPricingTabs() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Pricing Dinamico — 7 pestañas</span>
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-7 gap-1.5">
        {[
          { icon: Package, label: 'Productos', color: 'text-blue-600' },
          { icon: Store, label: 'Competidores', color: 'text-purple-600' },
          { icon: Bell, label: 'Alertas', color: 'text-red-500' },
          { icon: LineChart, label: 'Graficos', color: 'text-green-600' },
          { icon: Scan, label: 'Catalogo', color: 'text-orange-500' },
          { icon: Brain, label: 'Inferencia', color: 'text-pink-600' },
          { icon: Key, label: 'API', color: 'text-gray-600' },
        ].map(({ icon: Icon, label, color }) => (
          <div key={label} className="rounded-lg border bg-background p-2 text-center">
            <Icon className={`h-4 w-4 mx-auto mb-0.5 ${color}`} />
            <p className="text-[9px] font-medium text-foreground leading-tight">{label}</p>
          </div>
        ))}
      </div>
      <DiagramCaption>Navega entre pestañas para gestionar cada aspecto del pricing</DiagramCaption>
    </div>
  );
}

/** Pricing Dinámico — product table mockup */
export function DiagramDPProductTable() {
  const rows = [
    {
      name: 'Samsung Galaxy S24',
      sku: 'SAM-S24',
      price: 'Bs 4,200',
      comp1: 'Bs 3,950',
      diff1: '-5.9%',
      comp2: 'Bs 4,350',
      diff2: '+3.6%',
      confidence: 82,
    },
    {
      name: 'Laptop HP Pavilion',
      sku: 'HP-PAV15',
      price: 'Bs 4,100',
      comp1: 'Bs 3,800',
      diff1: '-7.3%',
      comp2: 'Bs 4,200',
      diff2: '+2.4%',
      confidence: 65,
    },
  ];
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Package className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Tabla de productos</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[10px]">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="p-1.5 text-left font-medium">Producto</th>
              <th className="p-1.5 text-right font-medium">Tu precio</th>
              <th className="p-1.5 text-right font-medium">TechStore</th>
              <th className="p-1.5 text-center font-medium">Dif.</th>
              <th className="p-1.5 text-right font-medium">ElGenioX</th>
              <th className="p-1.5 text-center font-medium">Dif.</th>
              <th className="p-1.5 text-center font-medium">Confianza</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.sku} className="border-b">
                <td className="p-1.5">
                  <div className="font-medium">{r.name}</div>
                  <div className="text-muted-foreground font-mono">{r.sku}</div>
                </td>
                <td className="p-1.5 text-right font-mono font-medium">{r.price}</td>
                <td className="p-1.5 text-right font-mono">{r.comp1}</td>
                <td className="p-1.5 text-center">
                  <MockBadge variant={r.diff1.startsWith('-') ? 'red' : 'green'}>
                    {r.diff1}
                  </MockBadge>
                </td>
                <td className="p-1.5 text-right font-mono">{r.comp2}</td>
                <td className="p-1.5 text-center">
                  <MockBadge variant={r.diff2.startsWith('+') ? 'green' : 'red'}>
                    {r.diff2}
                  </MockBadge>
                </td>
                <td className="p-1.5 text-center">
                  <div className="inline-flex items-center gap-1">
                    <div className="h-1.5 w-12 rounded-full bg-muted overflow-hidden">
                      <div
                        className={`h-full rounded-full ${r.confidence >= 70 ? 'bg-green-500' : 'bg-yellow-500'}`}
                        style={{ width: `${r.confidence}%` }}
                      />
                    </div>
                    <span className="text-[9px]">{r.confidence}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <DiagramCaption>
        Tus productos con precios de cada competidor y puntaje de confianza
      </DiagramCaption>
    </div>
  );
}

/** Pricing Dinámico — confidence score explanation */
export function DiagramConfidenceScore() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <HelpCircle className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Puntaje de confianza</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[
          {
            range: '70% - 100%',
            color: 'bg-green-500',
            textColor: 'text-green-700 dark:text-green-400',
            label: 'Datos suficientes',
            desc: 'Costo, competidores y ventas',
          },
          {
            range: '40% - 69%',
            color: 'bg-yellow-500',
            textColor: 'text-yellow-700 dark:text-yellow-400',
            label: 'Faltan algunos datos',
            desc: 'Puede faltar costo o ventas',
          },
          {
            range: '0% - 39%',
            color: 'bg-red-500',
            textColor: 'text-red-700 dark:text-red-400',
            label: 'Insuficiente',
            desc: 'Faltan datos criticos',
          },
        ].map(({ range, color, textColor, label, desc }) => (
          <div key={range} className="rounded-lg border bg-background p-2.5 text-center">
            <div className="h-2 w-16 rounded-full bg-muted mx-auto mb-1.5 overflow-hidden">
              <div
                className={`h-full rounded-full ${color}`}
                style={{
                  width: range.startsWith('7') ? '85%' : range.startsWith('4') ? '55%' : '25%',
                }}
              />
            </div>
            <p className={`text-[10px] font-semibold ${textColor}`}>{range}</p>
            <p className="text-[10px] font-medium text-foreground">{label}</p>
            <p className="text-[9px] text-muted-foreground">{desc}</p>
          </div>
        ))}
      </div>
      <DiagramCaption>
        Pasa el cursor sobre el indicador en la tabla para ver que datos faltan
      </DiagramCaption>
    </div>
  );
}

/** Pricing Dinámico — competitor card with mapping */
export function DiagramCompetitorCard() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Store className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Tarjeta de competidor</span>
      </div>
      <div className="rounded-lg border bg-background p-4 max-w-sm mx-auto">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-purple-500" />
            <div>
              <p className="text-xs font-semibold">TechStore Bolivia</p>
              <p className="text-[9px] text-muted-foreground flex items-center gap-1">
                <ExternalLink className="h-2.5 w-2.5" /> www.techstore.com.bo
              </p>
            </div>
          </div>
          <MockBadge variant="green">3 productos</MockBadge>
        </div>
        <div className="space-y-1.5 text-[10px]">
          <div className="flex items-center justify-between rounded border p-1.5 bg-muted/20">
            <div className="flex items-center gap-1.5">
              <Link className="h-3 w-3 text-blue-500" />
              <span>Samsung Galaxy S24</span>
            </div>
            <span className="font-mono text-muted-foreground">Bs 3,950</span>
          </div>
          <div className="flex items-center justify-between rounded border p-1.5 bg-muted/20">
            <div className="flex items-center gap-1.5">
              <Link className="h-3 w-3 text-blue-500" />
              <span>iPhone 15 Pro</span>
            </div>
            <span className="font-mono text-muted-foreground">Bs 8,200</span>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <MockButton variant="outline">
            <Link className="h-3 w-3" /> Agregar Mapeo
          </MockButton>
          <MockButton>
            <Scan className="h-3 w-3" /> Escanear Precios
          </MockButton>
        </div>
      </div>
      <DiagramCaption>
        Cada tienda competidora muestra sus productos mapeados y precios escaneados
      </DiagramCaption>
    </div>
  );
}

/** Pricing Dinámico — catalog scan flow */
export function DiagramCatalogScan() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Scan className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Escaneo de catálogo</span>
      </div>
      <div className="flex flex-col sm:flex-row items-center gap-2">
        <div className="rounded-lg border bg-background p-2.5 text-center flex-1 w-full">
          <Globe className="h-5 w-5 mx-auto mb-1 text-blue-500" />
          <p className="text-[10px] font-semibold">1. URL de la tienda</p>
          <p className="text-[9px] text-muted-foreground">www.competidor.com</p>
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 rotate-90 sm:rotate-0" />
        <div className="rounded-lg border bg-background p-2.5 text-center flex-1 w-full">
          <Settings className="h-5 w-5 mx-auto mb-1 text-purple-500" />
          <p className="text-[10px] font-semibold">2. Plataforma</p>
          <p className="text-[9px] text-muted-foreground">Shopify, VTEX, WooCommerce...</p>
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 rotate-90 sm:rotate-0" />
        <div className="rounded-lg border bg-background p-2.5 text-center flex-1 w-full">
          <Scan className="h-5 w-5 mx-auto mb-1 text-orange-500" />
          <p className="text-[10px] font-semibold">3. Escaneo</p>
          <div className="mt-1 h-1.5 w-full rounded-full bg-muted overflow-hidden">
            <div className="h-full w-3/4 rounded-full bg-orange-500 animate-pulse" />
          </div>
          <p className="text-[9px] text-muted-foreground mt-0.5">En progreso...</p>
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 rotate-90 sm:rotate-0" />
        <div className="rounded-lg border bg-background p-2.5 text-center flex-1 w-full">
          <CheckCircle2 className="h-5 w-5 mx-auto mb-1 text-green-500" />
          <p className="text-[10px] font-semibold">4. Resultados</p>
          <p className="text-[9px] text-muted-foreground">247 productos</p>
        </div>
      </div>
      <DiagramCaption>
        El sistema recorre toda la tienda y extrae productos con precios
      </DiagramCaption>
    </div>
  );
}

/** Pricing Dinámico — inference/matching */
export function DiagramInference() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Brain className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Prixia propone, tú confirmas</span>
      </div>
      <div className="rounded-lg border bg-background p-3 max-w-md mx-auto">
        <div className="flex items-center gap-3">
          {/* Competitor product */}
          <div className="flex-1 rounded border p-2 bg-muted/20 text-center">
            <Store className="h-4 w-4 mx-auto mb-1 text-purple-500" />
            <p className="text-[10px] font-semibold">Galaxy S24 Ultra 256GB</p>
            <p className="text-[9px] text-muted-foreground">TechStore · Bs 8,500</p>
          </div>
          {/* Match indicator */}
          <div className="text-center shrink-0">
            <div className="rounded-full bg-green-100 dark:bg-green-900/30 p-1.5 mb-0.5">
              <Sparkles className="h-4 w-4 text-green-600" />
            </div>
            <p className="text-[10px] font-bold text-green-600">92%</p>
          </div>
          {/* Your product */}
          <div className="flex-1 rounded border p-2 bg-muted/20 text-center">
            <Package className="h-4 w-4 mx-auto mb-1 text-blue-500" />
            <p className="text-[10px] font-semibold">Samsung S24 Ultra</p>
            <p className="text-[9px] text-muted-foreground">Tu producto · Bs 8,900</p>
          </div>
        </div>
        <div className="flex items-center gap-1 mt-2 text-[9px] text-muted-foreground justify-center">
          <span>Razones:</span>
          <MockBadge variant="default">Nombre similar</MockBadge>
          <MockBadge variant="default">Misma marca</MockBadge>
          <MockBadge variant="default">Precio cercano</MockBadge>
        </div>
        <div className="flex gap-2 mt-2 justify-center">
          <MockButton variant="outline">
            <Trash2 className="h-3 w-3" /> Rechazar
          </MockButton>
          <MockButton>
            <CheckCircle2 className="h-3 w-3" /> Aceptar
          </MockButton>
        </div>
      </div>
      <DiagramCaption>
        La IA sugiere matches con puntaje de confianza. Tu decides si aceptar o rechazar.
      </DiagramCaption>
    </div>
  );
}

/** Pricing Dinámico — alert types */
export function DiagramAlertTypes() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Bell className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Tipos de alerta</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[
          {
            level: 'Critica',
            color: 'border-red-300 bg-red-50 dark:bg-red-950/20',
            icon: AlertTriangle,
            iconColor: 'text-red-500',
            desc: 'Competidor bajo precio más del 15%',
            example: 'Galaxy S24: -22% en TechStore',
          },
          {
            level: 'Advertencia',
            color: 'border-yellow-300 bg-yellow-50 dark:bg-yellow-950/20',
            icon: AlertTriangle,
            iconColor: 'text-yellow-500',
            desc: 'Cambio entre 5% y 15%',
            example: 'Laptop HP: -8% en ElGenioX',
          },
          {
            level: 'Informativa',
            color: 'border-blue-300 bg-blue-50 dark:bg-blue-950/20',
            icon: Bell,
            iconColor: 'text-blue-500',
            desc: 'Actualizaciones generales',
            example: 'Nuevo precio detectado',
          },
        ].map(({ level, color, icon: Icon, iconColor, desc, example }) => (
          <div key={level} className={`rounded-lg border-2 p-2.5 ${color}`}>
            <Icon className={`h-5 w-5 mb-1 ${iconColor}`} />
            <p className="text-[10px] font-semibold text-foreground">{level}</p>
            <p className="text-[9px] text-muted-foreground">{desc}</p>
            <p className="text-[9px] italic text-muted-foreground mt-1">"{example}"</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Pricing Dinámico — price chart mockup */
export function DiagramPriceChart() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <LineChart className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">
          Grafico de evolución de precios
        </span>
      </div>
      <div className="rounded-lg border bg-background p-3">
        {/* Simulated chart area */}
        <div className="relative h-32 mb-2">
          {/* Y axis labels */}
          <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-[8px] text-muted-foreground w-10">
            <span>$5,000</span>
            <span>$4,000</span>
            <span>$3,000</span>
          </div>
          {/* Chart lines (CSS) */}
          <div className="ml-12 h-full relative">
            {/* Your price line - blue */}
            <svg
              className="absolute inset-0 w-full h-full"
              viewBox="0 0 200 100"
              preserveAspectRatio="none"
            >
              <polyline
                points="0,40 40,42 80,38 120,35 160,30 200,28"
                fill="none"
                stroke="rgb(59,130,246)"
                strokeWidth="2"
              />
              <polyline
                points="0,50 40,48 80,55 120,45 160,42 200,35"
                fill="none"
                stroke="rgb(249,115,22)"
                strokeWidth="2"
                strokeDasharray="4,4"
              />
              <polyline
                points="0,30 40,32 80,28 120,25 160,22 200,20"
                fill="none"
                stroke="rgb(34,197,94)"
                strokeWidth="2"
                strokeDasharray="6,3"
              />
            </svg>
          </div>
        </div>
        {/* Legend */}
        <div className="flex items-center justify-center gap-4 text-[9px]">
          <div className="flex items-center gap-1">
            <div className="h-0.5 w-4 bg-blue-500 rounded" />
            <span>Tu precio</span>
          </div>
          <div className="flex items-center gap-1">
            <div
              className="h-0.5 w-4 bg-green-500 rounded"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(to right, transparent, transparent 3px, white 3px, white 5px)',
              }}
            />
            <span>Recomendado</span>
          </div>
          <div className="flex items-center gap-1">
            <div
              className="h-0.5 w-4 bg-orange-500 rounded"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(to right, transparent, transparent 2px, white 2px, white 4px)',
              }}
            />
            <span>Min. competencia</span>
          </div>
        </div>
      </div>
      <DiagramCaption>
        Visualiza tendencias de precios en el tiempo para cada producto
      </DiagramCaption>
    </div>
  );
}

/** Pricing Dinámico — API endpoints */
export function DiagramAPIEndpoints() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Key className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Endpoints de la API</span>
      </div>
      <div className="space-y-2">
        <div className="rounded-lg border bg-background p-2.5">
          <div className="flex items-center gap-2 mb-1">
            <MockBadge variant="green">GET</MockBadge>
            <code className="text-[10px] font-mono text-foreground">
              /api/v1/price/{'{'}
              <span className="text-primary">SKU</span>
              {'}'}
            </code>
          </div>
          <p className="text-[9px] text-muted-foreground ml-1">
            Consulta precio de un producto por SKU
          </p>
        </div>
        <div className="rounded-lg border bg-background p-2.5">
          <div className="flex items-center gap-2 mb-1">
            <MockBadge variant="blue">POST</MockBadge>
            <code className="text-[10px] font-mono text-foreground">/api/v1/prices</code>
          </div>
          <p className="text-[9px] text-muted-foreground ml-1">
            Consulta masiva de hasta 100 SKUs a la vez
          </p>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 text-[9px] text-muted-foreground justify-center">
        <span>Ejemplos disponibles en:</span>
        <MockBadge>cURL</MockBadge>
        <MockBadge>JavaScript</MockBadge>
        <MockBadge>Python</MockBadge>
        <MockBadge>PHP</MockBadge>
      </div>
      <DiagramCaption>Usa X-API-Key en el header para autenticarte</DiagramCaption>
    </div>
  );
}

/** Pricing Dinámico — complete flow (8 steps) */
export function DiagramDPFullFlow() {
  const steps = [
    { icon: Upload, label: 'Cargar productos', color: 'text-blue-500' },
    { icon: Store, label: 'Registrar competidores', color: 'text-purple-500' },
    { icon: Scan, label: 'Escanear catalogos', color: 'text-orange-500' },
    { icon: Brain, label: 'Vincular (IA)', color: 'text-pink-500' },
    { icon: Eye, label: 'Ver precios', color: 'text-cyan-500' },
    { icon: Target, label: 'Calcular optimos', color: 'text-green-500' },
    { icon: ShoppingCart, label: 'Aplicar a tienda', color: 'text-amber-500' },
    { icon: Bell, label: 'Monitorear', color: 'text-red-500' },
  ];
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Flujo completo en 8 pasos</span>
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-8 gap-1">
        {steps.map(({ icon: Icon, label, color }, idx) => (
          <div key={label} className="text-center">
            <div className="rounded-full border bg-background p-2 mx-auto w-fit mb-1 relative">
              <Icon className={`h-4 w-4 ${color}`} />
              <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[8px] font-bold text-primary-foreground">
                {idx + 1}
              </span>
            </div>
            <p className="text-[8px] text-muted-foreground leading-tight">{label}</p>
          </div>
        ))}
      </div>
      <DiagramCaption>Desde cargar productos hasta monitoreo automático</DiagramCaption>
    </div>
  );
}

/** Pricing Dinámico — add product form */
export function DiagramAddProduct() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Package className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Formas de agregar productos</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[
          {
            icon: Pencil,
            label: 'Manual',
            desc: 'Botón "Nuevo Producto", completa nombre, SKU, precio, costo y stock',
            color: 'text-blue-500',
          },
          {
            icon: FileSpreadsheet,
            label: 'Importar CSV',
            desc: 'Sube un archivo con todos tus productos de una vez',
            color: 'text-green-500',
          },
          {
            icon: Scan,
            label: 'Desde catálogo',
            desc: 'Importa items de un escaneo previo como productos nuevos',
            color: 'text-orange-500',
          },
        ].map(({ icon: Icon, label, desc, color }) => (
          <div key={label} className="rounded-lg border bg-background p-2.5">
            <Icon className={`h-5 w-5 mb-1 ${color}`} />
            <p className="text-[10px] font-semibold text-foreground">{label}</p>
            <p className="text-[9px] text-muted-foreground">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── CAPÍTULOS 1-9: PLATAFORMA BASE ─────────────────────────── */

/** Cap 1 — Platform overview modules */
export function DiagramPlatformModules() {
  // 🔴 (20-oct) Mostraba los módulos de antes (Pricing Dinámico, Smart Pricing): hoy son Cotiza, Compite y Comercia.
  const modules = [
    {
      icon: FileText,
      label: 'Cotiza',
      desc: 'Cotizaciones, modelos y aprobaciones',
      color: 'text-blue-600',
    },
    {
      icon: Target,
      label: 'Compite',
      desc: 'Tus precios contra la competencia',
      color: 'text-pink-600',
    },
    {
      icon: Globe,
      label: 'Comercia',
      desc: 'El costo real de importar',
      color: 'text-orange-500',
    },
    {
      icon: Database,
      label: 'Datos maestros',
      desc: 'Divisas, países, materiales',
      color: 'text-purple-600',
    },
    {
      icon: Settings,
      label: 'Integraciones',
      desc: 'SAP, tiendas y la API',
      color: 'text-cyan-600',
    },
    {
      icon: Users,
      label: 'Mi empresa',
      desc: 'Usuarios, roles y equipos',
      color: 'text-green-600',
    },
  ];
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Layout className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Los módulos de la plataforma</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {modules.map(({ icon: Icon, label, desc, color }) => (
          <div key={label} className="rounded-lg border bg-background p-2.5 text-center">
            <Icon className={`h-5 w-5 mx-auto mb-1 ${color}`} />
            <p className="text-[10px] font-semibold text-foreground">{label}</p>
            <p className="text-[9px] text-muted-foreground">{desc}</p>
          </div>
        ))}
      </div>
      <DiagramCaption>
        Cada empresa tiene su espacio aislado; lo que ve cada persona depende de su rol
      </DiagramCaption>
    </div>
  );
}

/** Cap 2 — Login flow */
export function DiagramLoginFlow() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Lock className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Acceso al sistema</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border bg-background p-3">
          <Users className="h-5 w-5 text-blue-500 mb-1" />
          <p className="text-[10px] font-semibold">Iniciar sesion</p>
          <div className="mt-1.5 space-y-1">
            <div className="rounded border p-1 text-[9px] bg-muted/20">Email</div>
            <div className="rounded border p-1 text-[9px] bg-muted/20">Contrasena</div>
            <MockButton>Ingresar</MockButton>
          </div>
        </div>
        <div className="rounded-lg border bg-background p-3">
          <FileText className="h-5 w-5 text-green-500 mb-1" />
          <p className="text-[10px] font-semibold">Registrar empresa</p>
          <div className="mt-1.5 space-y-1 text-[9px] text-muted-foreground">
            <p>1. Datos del negocio</p>
            <p>2. Datos de la empresa</p>
            <p>3. Cuenta administrador</p>
            <MockButton variant="outline">Crear cuenta</MockButton>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Cap 3 — Roles grid */
export function DiagramRoles() {
  const roles = [
    {
      icon: Shield,
      label: 'Administrador',
      code: 'COMPANY_ADMIN',
      perms: 'Acceso total, gestiona modelos, datos y usuarios',
      color: 'border-blue-300 bg-blue-50 dark:bg-blue-950/20',
    },
    {
      icon: FileText,
      label: 'Vendedor',
      code: 'SALESPERSON',
      perms: 'Crea y edita sus cotizaciones',
      color: 'border-green-300 bg-green-50 dark:bg-green-950/20',
    },
    {
      icon: UserCheck,
      label: 'Aprobador',
      code: 'APPROVER',
      perms: 'Revisa y aprueba cotizaciones',
      color: 'border-purple-300 bg-purple-50 dark:bg-purple-950/20',
    },
    {
      icon: Settings,
      label: 'Admin Plataforma',
      code: 'PLATFORM_ADMIN',
      perms: 'Gestión a nivel de toda la plataforma',
      color: 'border-orange-300 bg-orange-50 dark:bg-orange-950/20',
    },
  ];
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Users className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Los 4 roles del sistema</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {roles.map(({ icon: Icon, label, code, perms, color }) => (
          <div key={code} className={`rounded-lg border-2 p-2.5 ${color}`}>
            <div className="flex items-center gap-1.5 mb-1">
              <Icon className="h-4 w-4" />
              <p className="text-[10px] font-semibold">{label}</p>
            </div>
            <p className="text-[9px] font-mono text-muted-foreground">{code}</p>
            <p className="text-[9px] text-muted-foreground mt-0.5">{perms}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Cap 4 — Dashboard layout */
export function DiagramDashboard() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Layout className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Inicio</span>
      </div>
      <div className="rounded-lg border bg-background p-3">
        <div className="rounded border p-2 bg-muted/20 mb-2">
          <p className="text-[10px] font-semibold">Bienvenido, Juan Martinez</p>
          <p className="text-[9px] text-muted-foreground">Administrador · Empresa XYZ</p>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[
            {
              icon: FileText,
              label: 'Cotizaciones',
              desc: 'Nueva o ver listado',
              color: 'text-blue-500',
            },
            {
              icon: Calculator,
              label: 'Modelos',
              desc: 'Gestionar modelos',
              color: 'text-purple-500',
            },
            {
              icon: UserCheck,
              label: 'Aprobaciones',
              desc: 'Pendientes de revision',
              color: 'text-green-500',
            },
          ].map(({ icon: Icon, label, desc, color }) => (
            <div
              key={label}
              className="rounded border p-2 text-center hover:bg-muted/30 cursor-pointer"
            >
              <Icon className={`h-5 w-5 mx-auto mb-0.5 ${color}`} />
              <p className="text-[9px] font-semibold">{label}</p>
              <p className="text-[8px] text-muted-foreground">{desc}</p>
            </div>
          ))}
        </div>
      </div>
      <DiagramCaption>El dashboard se adapta según tu rol</DiagramCaption>
    </div>
  );
}

/** Cap 5 — Master data tabs */
export function DiagramMasterData() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Database className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">4 pestañas de Datos Maestros</span>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {[
          {
            icon: DollarSign,
            label: 'Divisas',
            desc: 'USD, BOB, EUR + tipos de cambio',
            color: 'text-green-600',
          },
          {
            icon: GitBranch,
            label: 'Líneas de Negocio',
            desc: '3 niveles: Sector > Vertical > Producto',
            color: 'text-blue-600',
          },
          {
            icon: Globe,
            label: 'Paises',
            desc: 'Catálogo con codigos ISO',
            color: 'text-orange-500',
          },
          {
            icon: Package,
            label: 'Materiales',
            desc: 'Productos + aranceles + historial',
            color: 'text-purple-600',
          },
        ].map(({ icon: Icon, label, desc, color }) => (
          <div key={label} className="rounded-lg border bg-background p-2.5 text-center">
            <Icon className={`h-5 w-5 mx-auto mb-1 ${color}`} />
            <p className="text-[10px] font-semibold text-foreground">{label}</p>
            <p className="text-[9px] text-muted-foreground">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Cap 6 — Variable types */
export function DiagramVariableTypes() {
  const types = [
    { code: 'NUMBER', desc: 'Cantidades', example: '10 usuarios', color: 'blue' },
    { code: 'CURRENCY', desc: 'Dinero', example: '$1,500', color: 'green' },
    { code: 'PERCENTAGE', desc: 'Porcentaje', example: '15%', color: 'yellow' },
    { code: 'CALCULATED', desc: 'Formula', example: 'A * B', color: 'red' },
    { code: 'TIERED', desc: 'Por volumen', example: '1-10: $100', color: 'default' },
    { code: 'LOOKUP', desc: 'Tabla 2D', example: 'País x Nivel', color: 'default' },
  ];
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Hash className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">
          Principales tipos de variables
        </span>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
        {types.map(({ code, desc, example, color }) => (
          <div key={code} className="rounded-lg border bg-background p-2 text-center">
            <MockBadge variant={color}>{code}</MockBadge>
            <p className="text-[9px] font-medium mt-1">{desc}</p>
            <p className="text-[8px] text-muted-foreground italic">{example}</p>
          </div>
        ))}
      </div>
      <DiagramCaption>También disponibles: TEXT, BOOLEAN y ENUM</DiagramCaption>
    </div>
  );
}

/** Cap 7 — Quote 4-step wizard */
export function DiagramQuoteWizard() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Crear cotización en 4 pasos</span>
      </div>
      <div className="flex items-center gap-1">
        {[
          { step: 1, label: 'Cliente', desc: 'Nombre, email', icon: Users },
          { step: 2, label: 'Configuracion', desc: 'LOB, país, moneda', icon: Settings },
          { step: 3, label: 'Secciones', desc: 'Modelos + items', icon: Layers },
          { step: 4, label: 'Revision', desc: 'Margen y envío', icon: CheckCircle2 },
        ].map(({ step, label, desc, icon: Icon }, idx) => (
          <div key={step} className="flex items-center gap-1 flex-1">
            <div className="rounded-lg border bg-background p-2 flex-1 text-center">
              <div className="flex items-center justify-center gap-1 mb-0.5">
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground">
                  {step}
                </span>
                <Icon className="h-3.5 w-3.5 text-primary" />
              </div>
              <p className="text-[9px] font-semibold">{label}</p>
              <p className="text-[8px] text-muted-foreground">{desc}</p>
            </div>
            {idx < 3 && <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Cap 7 — Quote statuses */
export function DiagramQuoteStatuses() {
  const statuses = [
    { label: 'Borrador', code: 'DRAFT', color: 'gray', desc: 'Se puede editar' },
    { label: 'Pendiente', code: 'PENDING', color: 'yellow', desc: 'Esperando aprobación' },
    { label: 'Aprobada', code: 'APPROVED', color: 'green', desc: 'Aprobada' },
    { label: 'Rechazada', code: 'REJECTED', color: 'red', desc: 'Rechazada' },
    { label: 'Ajuste', code: 'ADJUSTMENT', color: 'blue', desc: 'Requiere cambios' },
  ];
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <ToggleLeft className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Estados de una cotización</span>
      </div>
      <div className="flex flex-wrap gap-1.5 justify-center">
        {statuses.map(({ label, color, desc }) => (
          <div key={label} className="rounded-lg border bg-background px-3 py-1.5 text-center">
            <MockBadge variant={color}>{label}</MockBadge>
            <p className="text-[8px] text-muted-foreground mt-0.5">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Cap 8 — Approval flow */
export function DiagramApprovalFlow() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <UserCheck className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Flujo de aprobación</span>
      </div>
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <div className="rounded-lg border bg-background p-2 flex-1 text-center">
            <Users className="h-4 w-4 mx-auto mb-0.5 text-blue-500" />
            <p className="text-[9px] font-semibold">Vendedor envia</p>
          </div>
          <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
          <div className="rounded-lg border bg-background p-2 flex-1 text-center">
            <Eye className="h-4 w-4 mx-auto mb-0.5 text-purple-500" />
            <p className="text-[9px] font-semibold">Aprobador revisa</p>
          </div>
          <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
          <div className="flex-1 space-y-1">
            <div className="rounded border bg-green-50 dark:bg-green-950/20 p-1.5 text-center">
              <p className="text-[9px] font-semibold text-green-700 dark:text-green-400">
                <CheckCircle2 className="h-3 w-3 inline mr-0.5" />
                Aprobar
              </p>
            </div>
            <div className="rounded border bg-red-50 dark:bg-red-950/20 p-1.5 text-center">
              <p className="text-[9px] font-semibold text-red-700 dark:text-red-400">
                <UserX className="h-3 w-3 inline mr-0.5" />
                Rechazar
              </p>
            </div>
            <div className="rounded border bg-amber-50 dark:bg-amber-950/20 p-1.5 text-center">
              <p className="text-[9px] font-semibold text-amber-700 dark:text-amber-400">
                <MessageSquare className="h-3 w-3 inline mr-0.5" />
                Pedir ajuste
              </p>
            </div>
          </div>
        </div>
      </div>
      <DiagramCaption>Si se pide ajuste, el vendedor edita y reenvia</DiagramCaption>
    </div>
  );
}

/** Cap 8 — Approval stats */
export function DiagramApprovalStats() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="grid grid-cols-4 gap-2">
        <MockCard>
          <p className="text-sm font-bold text-orange-500">8</p>
          <p className="text-[9px] text-muted-foreground">Pendientes</p>
        </MockCard>
        <MockCard>
          <p className="text-sm font-bold text-green-600">24</p>
          <p className="text-[9px] text-muted-foreground">Aprobadas</p>
        </MockCard>
        <MockCard>
          <p className="text-sm font-bold text-red-500">3</p>
          <p className="text-[9px] text-muted-foreground">Rechazadas</p>
        </MockCard>
        <MockCard>
          <p className="text-sm font-bold text-blue-500">2.3 días</p>
          <p className="text-[9px] text-muted-foreground">Tiempo prom.</p>
        </MockCard>
      </div>
      <DiagramCaption>Estadísticas en la parte superior del panel de aprobaciones</DiagramCaption>
    </div>
  );
}

/** Cap 9 — AI Assistant */
export function DiagramAIAssistant() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="rounded-lg border bg-background p-3 max-w-xs mx-auto">
        <div className="flex items-center gap-2 mb-2 pb-2 border-b">
          <Brain className="h-5 w-5 text-primary" />
          <p className="text-xs font-semibold">Asistente IA</p>
        </div>
        <div className="space-y-1.5 mb-2">
          <div className="rounded-lg bg-muted/30 p-1.5 text-[9px] text-muted-foreground max-w-[80%]">
            Como creo una cotizacion?
          </div>
          <div className="rounded-lg bg-primary/10 p-1.5 text-[9px] max-w-[80%] ml-auto">
            Para crear una cotizacion, ve a Cotizaciones y haz clic en "Nueva"...
          </div>
        </div>
        <div className="grid grid-cols-2 gap-1">
          {['Crear cotización?', 'Que es un modelo?', 'Aprobaciones?', 'Margenes?'].map((q) => (
            <button
              key={q}
              className="rounded border p-1 text-[8px] text-muted-foreground hover:bg-muted/30 text-left"
            >
              {q}
            </button>
          ))}
        </div>
      </div>
      <DiagramCaption>Disponible como botón flotante en todas las paginas</DiagramCaption>
    </div>
  );
}

/* ─── CLIENTES ───────────────────────────────────────────────── */

/** Customers table */
export function DiagramCustomersTable() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Users className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Tabla de clientes</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[10px]">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="p-1.5 text-left font-medium">Código</th>
              <th className="p-1.5 text-left font-medium">Nombre</th>
              <th className="p-1.5 text-center font-medium">Segmento</th>
              <th className="p-1.5 text-left font-medium">Email</th>
              <th className="p-1.5 text-center font-medium">Cotizaciones</th>
              <th className="p-1.5 text-center font-medium">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b">
              <td className="p-1.5 font-mono">CLI-001</td>
              <td className="p-1.5 font-medium">Banco Nacional</td>
              <td className="p-1.5 text-center">
                <MockBadge variant="blue">Enterprise</MockBadge>
              </td>
              <td className="p-1.5 text-muted-foreground">compras@banconacional.bo</td>
              <td className="p-1.5 text-center font-medium">12</td>
              <td className="p-1.5 text-center flex gap-1 justify-center">
                <Pencil className="h-3 w-3" />
                <Trash2 className="h-3 w-3 text-red-500" />
              </td>
            </tr>
            <tr className="border-b">
              <td className="p-1.5 font-mono">CLI-002</td>
              <td className="p-1.5 font-medium">TechSolutions SRL</td>
              <td className="p-1.5 text-center">
                <MockBadge variant="green">PyME</MockBadge>
              </td>
              <td className="p-1.5 text-muted-foreground">info@techsolutions.bo</td>
              <td className="p-1.5 text-center font-medium">5</td>
              <td className="p-1.5 text-center flex gap-1 justify-center">
                <Pencil className="h-3 w-3" />
                <Trash2 className="h-3 w-3 text-red-500" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <DiagramCaption>Busca por nombre, código, NIT o email. Filtra por segmento.</DiagramCaption>
    </div>
  );
}

/** Customer segments */
export function DiagramCustomerSegments() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Users className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Segmentos de clientes</span>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {[
          { label: 'Enterprise', color: 'blue', desc: 'Grandes empresas' },
          { label: 'PyME', color: 'green', desc: 'Pequenas y medianas' },
          { label: 'Gobierno', color: 'yellow', desc: 'Sector público' },
          { label: 'Startup', color: 'default', desc: 'Emprendimientos' },
        ].map(({ label, color, desc }) => (
          <div key={label} className="rounded-lg border bg-background p-2.5 text-center">
            <MockBadge variant={color}>{label}</MockBadge>
            <p className="text-[9px] text-muted-foreground mt-1">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Customer contacts */
export function DiagramCustomerContacts() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Users className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Contactos del cliente</span>
      </div>
      <div className="rounded-lg border bg-background p-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-semibold">Banco Nacional — Contactos</p>
          <MockButton variant="outline">
            <Users className="h-3 w-3" /> Agregar Contacto
          </MockButton>
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between rounded border p-2 bg-muted/20">
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center text-[9px] font-bold text-primary">
                JM
              </div>
              <div>
                <p className="text-[10px] font-medium">Juan Martinez</p>
                <p className="text-[9px] text-muted-foreground">
                  Gerente de Compras · juan@banconacional.bo
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <MockBadge variant="green">Principal</MockBadge>
              <Pencil className="h-3 w-3 text-muted-foreground" />
            </div>
          </div>
          <div className="flex items-center justify-between rounded border p-2 bg-muted/20">
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-[9px] font-bold text-muted-foreground">
                ML
              </div>
              <div>
                <p className="text-[10px] font-medium">Maria Lopez</p>
                <p className="text-[9px] text-muted-foreground">
                  Jefa de TI · maria@banconacional.bo
                </p>
              </div>
            </div>
            <Pencil className="h-3 w-3 text-muted-foreground" />
          </div>
        </div>
      </div>
      <DiagramCaption>Expande un cliente para ver y gestionar sus contactos</DiagramCaption>
    </div>
  );
}

/* ─── IMPORTACIONES ──────────────────────────────────────────── */

/** Import tabs */
export function DiagramImportTabs() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <ShoppingCart className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">
          Modulo de Importaciones — 5 pestañas
        </span>
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        {[
          { icon: FileText, label: 'Importaciones', color: 'text-blue-600' },
          { icon: DollarSign, label: 'Cotizaciones', color: 'text-green-600' },
          { icon: Package, label: 'Disponibilidad', color: 'text-orange-500' },
          { icon: Calculator, label: 'Calculadora', color: 'text-purple-600' },
          { icon: BarChart3, label: 'KPIs', color: 'text-pink-600' },
        ].map(({ icon: Icon, label, color }) => (
          <div key={label} className="rounded-lg border bg-background p-2 text-center">
            <Icon className={`h-4 w-4 mx-auto mb-0.5 ${color}`} />
            <p className="text-[9px] font-medium text-foreground">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Import statuses */
export function DiagramImportStatuses() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <ToggleLeft className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Estados de una importación</span>
      </div>
      <div className="flex flex-wrap gap-1 justify-center">
        {[
          { label: 'Borrador', color: 'gray' },
          { label: 'Confirmada', color: 'blue' },
          { label: 'Embarcada', color: 'default' },
          { label: 'En Transito', color: 'yellow' },
          { label: 'En Aduana', color: 'red' },
          { label: 'Entregada', color: 'green' },
        ].map(({ label, color }, idx, arr) => (
          <div key={label} className="flex items-center gap-1">
            <MockBadge variant={color}>{label}</MockBadge>
            {idx < arr.length - 1 && <ArrowRight className="h-3 w-3 text-muted-foreground" />}
          </div>
        ))}
      </div>
      <DiagramCaption>La orden avanza de estado según el progreso real del envío</DiagramCaption>
    </div>
  );
}

/** Import detail tabs */
export function DiagramImportDetailTabs() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Eye className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Detalle de orden — 6 pestañas</span>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
        {[
          { icon: FileText, label: 'Info', desc: 'Resumen y costos' },
          { icon: Package, label: 'Contenedores', desc: 'Carga fisica' },
          { icon: Rows3, label: 'Productos', desc: 'Items importados' },
          { icon: Clock, label: 'Tracking', desc: 'Seguimiento' },
          { icon: Upload, label: 'Documentos', desc: 'BL, facturas...' },
          { icon: Globe, label: 'Journey Map', desc: 'Mapa de ruta' },
        ].map(({ icon: Icon, label, desc }) => (
          <div key={label} className="rounded-lg border bg-background p-2 text-center">
            <Icon className="h-4 w-4 mx-auto mb-0.5 text-primary" />
            <p className="text-[9px] font-semibold">{label}</p>
            <p className="text-[8px] text-muted-foreground">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── INTEGRACIONES / API KEYS / TIENDAS ─────────────────────── */

/** Store connections */
export function DiagramStoreConnections() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Store className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Tiendas conectadas</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[10px]">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="p-1.5 text-left font-medium">Tienda</th>
              <th className="p-1.5 text-left font-medium">URL</th>
              <th className="p-1.5 text-center font-medium">Estado</th>
              <th className="p-1.5 text-center font-medium">Último push</th>
              <th className="p-1.5 text-center font-medium">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b">
              <td className="p-1.5 font-medium">Mi Tienda Shopify</td>
              <td className="p-1.5 text-muted-foreground">mitienda.myshopify.com</td>
              <td className="p-1.5 text-center">
                <MockBadge variant="green">Activa</MockBadge>
              </td>
              <td className="p-1.5 text-center text-muted-foreground">hace 2h</td>
              <td className="p-1.5 text-center flex gap-1 justify-center">
                <Wifi className="h-3 w-3 text-blue-500" />
                <Pencil className="h-3 w-3 text-foreground" />
                <Unplug className="h-3 w-3 text-red-500" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <DiagramCaption>Conecta tu tienda para enviar precios automáticamente</DiagramCaption>
    </div>
  );
}

/** API Keys management */
export function DiagramAPIKeysManagement() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Key className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Gestión de API Keys</span>
      </div>
      <div className="space-y-2">
        <div className="rounded-lg border bg-background p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold">Claves activas</p>
            <MockButton>
              <Key className="h-3 w-3" /> Crear API Key
            </MockButton>
          </div>
          <table className="w-full text-[10px]">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="p-1 text-left">Nombre</th>
                <th className="p-1 text-left">Prefijo</th>
                <th className="p-1 text-center">Estado</th>
                <th className="p-1 text-right">Peticiones</th>
                <th className="p-1 text-center">Acción</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="p-1">Producción</td>
                <td className="p-1 font-mono text-muted-foreground">pk_live_8f3x...</td>
                <td className="p-1 text-center">
                  <MockBadge variant="green">Activa</MockBadge>
                </td>
                <td className="p-1 text-right">1,247</td>
                <td className="p-1 text-center">
                  <MockBadge variant="red">Revocar</MockBadge>
                </td>
              </tr>
              <tr>
                <td className="p-1">Testing</td>
                <td className="p-1 font-mono text-muted-foreground">pk_test_2a9k...</td>
                <td className="p-1 text-center">
                  <MockBadge variant="green">Activa</MockBadge>
                </td>
                <td className="p-1 text-right">89</td>
                <td className="p-1 text-center">
                  <MockBadge variant="red">Revocar</MockBadge>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/** API Playground */
export function DiagramAPIPlayground() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Play className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Playground para probar la API</span>
      </div>
      <div className="rounded-lg border bg-background p-3 max-w-sm mx-auto">
        <div className="space-y-2 text-[10px]">
          <div>
            <label className="text-muted-foreground">API Key</label>
            <div className="rounded border p-1.5 bg-muted/20 font-mono mt-0.5">pk_live_8f3x...</div>
          </div>
          <div>
            <label className="text-muted-foreground">Endpoint</label>
            <div className="rounded border p-1.5 bg-muted/20 mt-0.5 flex items-center gap-1">
              <MockBadge variant="green">GET</MockBadge>
              <span className="font-mono">/api/v1/price/SAM-S24</span>
            </div>
          </div>
          <MockButton>
            <Play className="h-3 w-3" /> Enviar petición
          </MockButton>
          <div className="rounded border p-2 bg-green-50 dark:bg-green-950/20">
            <p className="text-[9px] font-semibold text-green-700 dark:text-green-400 mb-1">
              200 OK
            </p>
            <pre className="text-[8px] text-muted-foreground font-mono">{`{
  "sku": "SAM-S24",
  "price": 3950,
  "confidence": 82
}`}</pre>
          </div>
        </div>
      </div>
      <DiagramCaption>Prueba tus consultas antes de integrar con tu sistema</DiagramCaption>
    </div>
  );
}

/** Integration flow — 4 data types */
export function DiagramIntegrationDataTypes() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Code className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">
          Tipos de datos que puedes enviar via API
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          {
            icon: Package,
            label: 'Productos',
            desc: 'Nombre, SKU, precio, costo',
            color: 'text-blue-500',
          },
          {
            icon: Rows3,
            label: 'Inventario',
            desc: 'Stock, nivel mínimo',
            color: 'text-green-500',
          },
          {
            icon: ShoppingCart,
            label: 'Ventas',
            desc: 'Historial de ventas',
            color: 'text-purple-500',
          },
          {
            icon: Store,
            label: 'Precios competencia',
            desc: 'Precios de otros',
            color: 'text-orange-500',
          },
        ].map(({ icon: Icon, label, desc, color }) => (
          <div key={label} className="rounded-lg border bg-background p-2.5 text-center">
            <Icon className={`h-5 w-5 mx-auto mb-1 ${color}`} />
            <p className="text-[10px] font-semibold">{label}</p>
            <p className="text-[9px] text-muted-foreground">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Push history */
export function DiagramPushHistory() {
  return (
    <div className="my-4 rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <History className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Historial de envios a tienda</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[10px]">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="p-1.5 text-left font-medium">Tienda</th>
              <th className="p-1.5 text-center font-medium">Tipo</th>
              <th className="p-1.5 text-center font-medium">Productos</th>
              <th className="p-1.5 text-center font-medium">Estado</th>
              <th className="p-1.5 text-right font-medium">Fecha</th>
              <th className="p-1.5 text-center font-medium">Acción</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b">
              <td className="p-1.5">Mi Tienda</td>
              <td className="p-1.5 text-center">
                <MockBadge variant="default">Smart Pricing</MockBadge>
              </td>
              <td className="p-1.5 text-center font-medium">45</td>
              <td className="p-1.5 text-center">
                <MockBadge variant="green">Aplicado</MockBadge>
              </td>
              <td className="p-1.5 text-right text-muted-foreground">hace 2h</td>
              <td className="p-1.5 text-center">
                <RotateCcw className="h-3 w-3 text-orange-500 mx-auto" />
              </td>
            </tr>
            <tr className="border-b">
              <td className="p-1.5">Mi Tienda</td>
              <td className="p-1.5 text-center">
                <MockBadge variant="blue">Regla</MockBadge>
              </td>
              <td className="p-1.5 text-center font-medium">12</td>
              <td className="p-1.5 text-center">
                <MockBadge variant="yellow">Revertido</MockBadge>
              </td>
              <td className="p-1.5 text-right text-muted-foreground">ayer</td>
              <td className="p-1.5 text-center">—</td>
            </tr>
          </tbody>
        </table>
      </div>
      <DiagramCaption>
        Filtra por tipo (Smart Pricing, Regla, Descuento, Manual) y estado
      </DiagramCaption>
    </div>
  );
}

/* ─── Registry ────────────────────────────────────────────────── */

export const chapterIllustrations: Record<string, React.ComponentType> = {
  'smart-pricing-tabs': DiagramSmartPricingTabs,
  'catalog-cards': DiagramCatalogCards,
  'catalog-table': DiagramCatalogTable,
  strategies: DiagramStrategies,
  'compare-cards': DiagramCompareCards,
  'linked-products': DiagramLinkedProducts,
  'discount-form': DiagramDiscountForm,
  'discount-statuses': DiagramDiscountStatuses,
  'discount-actions': DiagramDiscountActions,
  'full-flow': DiagramFullFlow,
  // Pricing Dinámico
  'dp-tabs': DiagramDynamicPricingTabs,
  'dp-product-table': DiagramDPProductTable,
  'dp-confidence': DiagramConfidenceScore,
  'dp-competitor-card': DiagramCompetitorCard,
  'dp-catalog-scan': DiagramCatalogScan,
  'dp-inference': DiagramInference,
  'dp-alert-types': DiagramAlertTypes,
  'dp-price-chart': DiagramPriceChart,
  'dp-api-endpoints': DiagramAPIEndpoints,
  'dp-full-flow': DiagramDPFullFlow,
  'dp-add-product': DiagramAddProduct,
  // Capítulos 1-9
  'platform-modules': DiagramPlatformModules,
  'login-flow': DiagramLoginFlow,
  roles: DiagramRoles,
  dashboard: DiagramDashboard,
  'master-data': DiagramMasterData,
  'variable-types': DiagramVariableTypes,
  'quote-wizard': DiagramQuoteWizard,
  'quote-statuses': DiagramQuoteStatuses,
  'approval-flow': DiagramApprovalFlow,
  'approval-stats': DiagramApprovalStats,
  'ai-assistant': DiagramAIAssistant,
  // Integraciones
  'store-connections': DiagramStoreConnections,
  'api-keys-mgmt': DiagramAPIKeysManagement,
  'api-playground': DiagramAPIPlayground,
  'integration-data-types': DiagramIntegrationDataTypes,
  'push-history': DiagramPushHistory,
  // Clientes
  'customers-table': DiagramCustomersTable,
  'customer-segments': DiagramCustomerSegments,
  'customer-contacts': DiagramCustomerContacts,
  // Importaciones
  'import-tabs': DiagramImportTabs,
  'import-statuses': DiagramImportStatuses,
  'import-detail-tabs': DiagramImportDetailTabs,
};
